"use client";

import { useEffect, useState } from "react";

// Route-level error boundary. Replaces Next's bare fullscreen error with a
// readable screen that shows the actual message + stack, persists it to
// localStorage (so it can be read back later even if dismissed fast), and lets
// the player recover instead of being stuck.
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const details = [
    `Message: ${error?.message || "(no message)"}`,
    error?.digest ? `Digest: ${error.digest}` : null,
    `When: ${new Date().toISOString()}`,
    `URL: ${typeof window !== "undefined" ? window.location.href : ""}`,
    "",
    error?.stack || "(no stack)",
  ]
    .filter(Boolean)
    .join("\n");

  useEffect(() => {
    // Log + persist so the error survives a fast dismissal / page change.
    console.error("[HeroOrAVillain] crashed:", error);
    try {
      window.localStorage.setItem(
        "heroOrAVillain:lastError",
        JSON.stringify({
          message: error?.message,
          digest: error?.digest,
          stack: error?.stack,
          url: window.location.href,
          at: new Date().toISOString(),
        }),
      );
    } catch {
      /* storage may be unavailable */
    }
  }, [error]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(details);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-5 px-4 py-10 text-center">
      <div className="text-5xl">💥</div>
      <h1 className="text-[clamp(1.5rem,4vw,2.5rem)] font-extrabold">
        Something broke
      </h1>
      <p className="max-w-md text-white/60">
        The game hit an error. The details are below — please screenshot or copy
        them so this can be fixed.
      </p>

      <pre className="max-h-64 w-full max-w-2xl overflow-auto rounded-xl border border-red-500/30 bg-black/50 p-4 text-left text-xs text-red-200">
        {details}
      </pre>

      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          onClick={copy}
          className="rounded-full border border-white/20 px-6 py-3 text-sm font-bold uppercase tracking-widest text-white/80 transition hover:border-white/40"
        >
          {copied ? "Copied!" : "Copy details"}
        </button>
        <button
          onClick={reset}
          className="rounded-full bg-accent px-6 py-3 text-sm font-bold uppercase tracking-widest text-white shadow-[0_0_30px_rgba(124,58,237,0.5)] transition hover:brightness-110"
        >
          Try again
        </button>
        <a
          href="/"
          className="rounded-full border border-white/20 px-6 py-3 text-sm font-bold uppercase tracking-widest text-white/80 transition hover:border-white/40"
        >
          Back to start
        </a>
      </div>
    </main>
  );
}
